import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PasswordField from "components/PasswordField";
import api from "utils/api";
import { validatePersonNames } from "utils/personName";
import { setUser } from "state";
import "styles/auth.css";

export const PasswordSettings = ({ user, embedded = false }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState(null);

  const changePassword = async (e) => {
    e.preventDefault();
    setPasswordMsg(null);
    if (newPassword.length < 8) {
      setPasswordMsg({
        kind: "error",
        text: "Das Passwort muss mindestens 8 Zeichen lang sein.",
      });
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setPasswordMsg({ kind: "error", text: "Die Passwörter stimmen nicht überein." });
      return;
    }
    setPasswordSaving(true);
    try {
      const { data } = await api.post("/auth/profile/password-change", {
        currentPassword,
        newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setPasswordMsg({ kind: "success", text: data.message });
    } catch (err) {
      setPasswordMsg({
        kind: "error",
        text: err?.response?.data?.message || "Passwortänderung fehlgeschlagen.",
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  const content = (
    <>
      {embedded ? (
        <h3 id="konto-password-heading" className="konto-profile-sub">
          Passwort
        </h3>
      ) : (
        <h2 id="konto-password-heading">Passwort</h2>
      )}
      <div className="auth-alert is-info">
        Falls dein Account von uns manuell erstellt worden ist, lautet dein
        Passwort wie folgt: <strong>12345678</strong>
      </div>
      {passwordMsg && (
        <div className={`auth-alert is-${passwordMsg.kind}`}>{passwordMsg.text}</div>
      )}
      <form
        className="auth-form konto-profile-form konto-password-form"
        onSubmit={changePassword}
        autoComplete="off"
      >
        <input
          type="text"
          name="username"
          className="konto-profile-autofill-anchor"
          value={user?.email || ""}
          readOnly
          tabIndex={-1}
          aria-hidden="true"
          autoComplete="username"
        />
        <PasswordField
          id="profile-password-current"
          label="Aktuelles Passwort"
          name="current-password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
        <PasswordField
          id="profile-password-new"
          label="Neues Passwort"
          name="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />
        <PasswordField
          id="profile-password-confirm"
          label="Neues Passwort wiederholen"
          name="new-password-confirm"
          value={newPasswordConfirm}
          onChange={(e) => setNewPasswordConfirm(e.target.value)}
          autoComplete="off"
          minLength={8}
          required
        />
        <div className="konto-profile-actions">
          <button type="submit" className="btn-primary" disabled={passwordSaving}>
            {passwordSaving ? "Wird gespeichert…" : "Passwort ändern"}
          </button>
        </div>
      </form>
    </>
  );

  if (embedded) {
    return (
      <div className="konto-password-embedded" aria-labelledby="konto-password-heading">
        {content}
      </div>
    );
  }

  return (
    <section className="konto-profile" aria-labelledby="konto-password-heading">
      {content}
    </section>
  );
};

const emptyAddress = () => ({
  companyName: "",
  street: "",
  postalCode: "",
  city: "",
});

const splitAddressLine = (value) => {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const match = text.match(/^(.*?)(?:,\s*|\s+)(\d{5})\s+(.+)$/);
  if (!match) return { street: text, postalCode: "", city: "" };
  return {
    street: match[1].replace(/,\s*$/, "").trim(),
    postalCode: match[2],
    city: match[3].trim(),
  };
};

const addressFromStored = (addr) => {
  if (!addr) return emptyAddress();
  if (typeof addr === "string") {
    return { ...emptyAddress(), ...splitAddressLine(addr) };
  }
  const street = String(addr.street || "").trim();
  const postalCode = String(addr.postalCode || "").trim();
  const city = String(addr.city || "").trim();
  if (street || postalCode || city) {
    return {
      companyName: String(addr.companyName || "").trim(),
      street,
      postalCode,
      city,
    };
  }
  return {
    companyName: String(addr.companyName || "").trim(),
    ...splitAddressLine(addr.fullAddress || addr.address || ""),
  };
};

const AddressInfo = ({ id, label, children }) => (
  <span className="konto-profile-info">
    <button
      type="button"
      className="konto-profile-info__button"
      aria-label={`Informationen zur ${label}`}
      aria-describedby={id}
    >
      ?
    </button>
    <span id={id} role="tooltip" className="konto-profile-info__content">
      {children}
    </span>
  </span>
);

const ProfileSettings = ({ brand, passwordSection = null }) => {
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState(emptyAddress);
  const [invoiceAddress, setInvoiceAddress] = useState(emptyAddress);
  const [newEmail, setNewEmail] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null);
  const [emailMsg, setEmailMsg] = useState(null);

  useEffect(() => {
    if (!user) return;
    setFirstName(user.firstName || "");
    setLastName(user.lastName || "");
    setPhone(user.phone || "");
    setAddress(addressFromStored(user.customerAddress));
    setInvoiceAddress(addressFromStored(user.rechnungAddress));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled && data.user) {
          dispatch(setUser({ user: data.user }));
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg(null);
    const nameError = validatePersonNames(firstName, lastName);
    if (nameError) {
      setProfileMsg({ kind: "error", text: nameError });
      return;
    }
    setProfileSaving(true);
    try {
      const { data } = await api.patch("/auth/profile", {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        customerAddress: {
          street: address.street.trim(),
          postalCode: address.postalCode.trim(),
          city: address.city.trim(),
          fullAddress: [
            address.street.trim(),
            [address.postalCode.trim(), address.city.trim()].filter(Boolean).join(" "),
          ]
            .filter(Boolean)
            .join(", "),
        },
        rechnungAddress: {
          companyName: invoiceAddress.companyName.trim(),
          street: invoiceAddress.street.trim(),
          postalCode: invoiceAddress.postalCode.trim(),
          city: invoiceAddress.city.trim(),
          fullAddress: [
            invoiceAddress.street.trim(),
            [invoiceAddress.postalCode.trim(), invoiceAddress.city.trim()]
              .filter(Boolean)
              .join(" "),
          ]
            .filter(Boolean)
            .join(", "),
        },
      });
      dispatch(setUser({ user: data.user }));
      setProfileMsg({ kind: "success", text: data.message });
    } catch (err) {
      setProfileMsg({
        kind: "error",
        text: err?.response?.data?.message || "Speichern fehlgeschlagen.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const requestEmailChange = async (e) => {
    e.preventDefault();
    const trimmed = newEmail.trim();
    if (!trimmed) return;
    setEmailSaving(true);
    setEmailMsg(null);
    try {
      const body = { newEmail: trimmed };
      if (brand) body.brand = brand;
      const { data } = await api.post("/auth/profile/email-change", body);
      dispatch(setUser({ user: data.user }));
      setNewEmail("");
      setEmailMsg({ kind: "success", text: data.message });
    } catch (err) {
      setEmailMsg({
        kind: "error",
        text: err?.response?.data?.message || "Anfrage fehlgeschlagen.",
      });
    } finally {
      setEmailSaving(false);
    }
  };

  return (
    <section className="konto-profile" aria-labelledby="konto-profile-heading">
      <h2 id="konto-profile-heading">Meine Daten</h2>

      {user?.pendingEmail && (
        <div className="auth-alert is-info konto-profile-pending">
          Bestätigung ausstehend: Wir haben einen Link an{" "}
          <strong>{user.pendingEmail}</strong> gesendet. Deine E-Mail-Adresse
          ändert sich erst nach dem Klick in dieser E-Mail.
        </div>
      )}

      {profileMsg && (
        <div className={`auth-alert is-${profileMsg.kind}`}>{profileMsg.text}</div>
      )}

      <form className="auth-form konto-profile-form" onSubmit={saveProfile}>
        <div className="row">
          <div>
            <label htmlFor="profile-first">Vorname</label>
            <input
              id="profile-first"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
              required
            />
          </div>
          <div>
            <label htmlFor="profile-last">Nachname</label>
            <input
              id="profile-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="profile-phone">Telefon (optional)</label>
          <input
            id="profile-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
        </div>
        <h3 className="konto-profile-sub konto-profile-sub--with-info">
          Massageadresse
          <AddressInfo id="konto-massage-address-info" label="Massageadresse">
            Wenn du hier deine Massageadresse speicherst, wird sie bei zukünftigen Buchungen automatisch ausgefüllt.
          </AddressInfo>
        </h3>
        <div>
          <label htmlFor="profile-street">Straße &amp; Hausnummer</label>
          <input
            id="profile-street"
            value={address.street}
            onChange={(e) => setAddress((current) => ({ ...current, street: e.target.value }))}
            autoComplete="street-address"
            placeholder="Straße Hausnummer"
          />
        </div>
        <div className="row">
          <div>
            <label htmlFor="profile-plz">PLZ</label>
            <input
              id="profile-plz"
              value={address.postalCode}
              onChange={(e) =>
                setAddress((current) => ({ ...current, postalCode: e.target.value }))
              }
              autoComplete="postal-code"
              maxLength={10}
            />
          </div>
          <div>
            <label htmlFor="profile-city">Ort</label>
            <input
              id="profile-city"
              value={address.city}
              onChange={(e) => setAddress((current) => ({ ...current, city: e.target.value }))}
              autoComplete="address-level2"
            />
          </div>
        </div>
        <h3 className="konto-profile-sub konto-profile-sub--with-info">
          Rechnungsadresse
          <AddressInfo id="konto-invoice-address-info" label="Rechnungsadresse">
            Trage hier die Anschrift ein, die auf deinen Rechnungen stehen soll. Sie kann von der Massageadresse abweichen.
          </AddressInfo>
        </h3>
        <div>
          <label htmlFor="profile-invoice-company">Unternehmensname</label>
          <input
            id="profile-invoice-company"
            value={invoiceAddress.companyName}
            onChange={(e) =>
              setInvoiceAddress((current) => ({ ...current, companyName: e.target.value }))
            }
            autoComplete="organization"
          />
        </div>
        <div>
          <label htmlFor="profile-invoice-street">Straße &amp; Hausnummer</label>
          <input
            id="profile-invoice-street"
            value={invoiceAddress.street}
            onChange={(e) =>
              setInvoiceAddress((current) => ({ ...current, street: e.target.value }))
            }
            autoComplete="section-billing street-address"
            placeholder="Straße Hausnummer"
          />
        </div>
        <div className="row">
          <div>
            <label htmlFor="profile-invoice-plz">PLZ</label>
            <input
              id="profile-invoice-plz"
              value={invoiceAddress.postalCode}
              onChange={(e) =>
                setInvoiceAddress((current) => ({ ...current, postalCode: e.target.value }))
              }
              autoComplete="section-billing postal-code"
              maxLength={10}
            />
          </div>
          <div>
            <label htmlFor="profile-invoice-city">Ort</label>
            <input
              id="profile-invoice-city"
              value={invoiceAddress.city}
              onChange={(e) =>
                setInvoiceAddress((current) => ({ ...current, city: e.target.value }))
              }
              autoComplete="section-billing address-level2"
            />
          </div>
        </div>
        <div className="konto-profile-actions">
          <button type="submit" className="btn-primary" disabled={profileSaving}>
            {profileSaving ? "Wird gespeichert…" : "Daten speichern"}
          </button>
        </div>
      </form>

      {passwordSection}

      <h3 className="konto-profile-sub">E-Mail-Adresse</h3>
      {emailMsg && (
        <div className={`auth-alert is-${emailMsg.kind}`}>{emailMsg.text}</div>
      )}
      <form
        className="auth-form konto-profile-form"
        onSubmit={requestEmailChange}
        autoComplete="off"
      >
        <div>
          <label htmlFor="profile-email-current">Aktuelle E-Mail</label>
          <input
            id="profile-email-current"
            name="username"
            type="email"
            value={user?.email || ""}
            readOnly
            aria-readonly="true"
            autoComplete="username"
          />
        </div>
        <div>
          <label htmlFor="profile-email-new">Neue E-Mail</label>
          <input
            id="profile-email-new"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            autoComplete="email"
            placeholder="neue@adresse.de"
            required
          />
        </div>
        <p className="konto-profile-hint">
          Wir senden einen Bestätigungslink an die neue E-Mail-Adresse. Die Änderung
          wird erst nach dem Klick wirksam.
        </p>
        <div className="konto-profile-actions">
          <button type="submit" className="btn-primary" disabled={emailSaving}>
            {emailSaving ? "Wird gesendet…" : "Neue E-Mail bestätigen lassen"}
          </button>
        </div>
      </form>
    </section>
  );
};

export default ProfileSettings;
